import { useMemo, useState } from 'react';
import {
  datumOmgedraaid,
  facetten,
  filterbaar,
  pasFiltersToe,
  sorteer,
  standNaarParameters,
  suggesties as berekenSuggesties,
  type Context,
  type Facet,
  type FilterStand,
  type Kolom,
  type Lijst,
  type Sortering,
  type Suggesties,
} from '@xxtract/tabelfilters';

/** Wat de data-hooks van de stand lezen (een `TabelStand` voldoet). */
export interface StandInvoer {
  filters: FilterStand;
  sortering: Sortering;
  pagina: number;
  paginagrootte?: number;
}

/** Wat de filterbalk nodig heeft, voor browser- en serverlijsten gelijk. */
export interface TabelData {
  keuzes: Record<string, Facet[]>;
  suggesties: (sleutel: string, tekst: string) => Suggesties;
  afgekapt: boolean;
  zonderAantallen: boolean;
  /** 0 = direct filteren (browser), 300 = na 300 ms stilte (server, §3). */
  vertraging: number;
  totaal: number;
  pagina: number;
  paginas: number;
  /** Server: het tekstveld met focus vraagt suggesties mee in dezelfde vraag. */
  opTekstFocus?: (sleutel: string | null) => void;
}

const GEEN: Suggesties = { waarden: [], meer: false };

function keuzelijstSleutels<R, C extends Context>(lijst: Lijst<R, C>): string[] {
  return lijst.kolommen.filter((k: Kolom<R, C>) => k.type === 'keuzelijst' && filterbaar(k)).map((k) => k.sleutel);
}

function paginas(totaal: number, paginagrootte?: number) {
  return paginagrootte ? Math.max(1, Math.ceil(totaal / paginagrootte)) : 1;
}

/**
 * Lijst in de browser: rekent zelf filters, trapsgewijze keuzes, suggesties, sortering en de pagina.
 * `context` moet stabiel zijn (`useMemo`), anders rekent de hook bij elke render opnieuw.
 */
export function useTabelData<R, C extends Context>(rijen: readonly R[] | undefined, lijst: Lijst<R, C>, stand: StandInvoer, context: C): TabelData & { rijen: R[] } {
  const { filters, sortering, pagina, paginagrootte } = stand;
  return useMemo(() => {
    const alle = rijen ?? [];
    const gesorteerd = sorteer(pasFiltersToe(alle, lijst, filters, context), lijst, sortering, context);
    const aantalPaginas = paginas(gesorteerd.length, paginagrootte);
    // Een bewaarde pagina die niet meer bestaat wordt de laatste (§7).
    const huidig = Math.min(Math.max(pagina, 1), aantalPaginas);
    const zichtbaar = paginagrootte ? gesorteerd.slice((huidig - 1) * paginagrootte, huidig * paginagrootte) : gesorteerd;
    return {
      rijen: zichtbaar,
      totaal: gesorteerd.length,
      pagina: huidig,
      paginas: aantalPaginas,
      keuzes: facetten(alle, lijst, filters, keuzelijstSleutels(lijst), context),
      suggesties: (sleutel: string, tekst: string) => berekenSuggesties(alle, lijst, filters, sleutel, tekst, context),
      afgekapt: false,
      zonderAantallen: false,
      vertraging: 0,
    };
  }, [rijen, lijst, filters, sortering, pagina, paginagrootte, context]);
}

/** Wat het endpoint teruggeeft, in welke antwoordvorm de route het ook zet (§8). */
export interface ServerAntwoord {
  totaal?: number;
  facetten?: Record<string, Facet[]>;
  suggesties?: Suggesties;
  afgekapt?: boolean;
}

/**
 * Lijst op de server: zet de stand om in queryparameters (§7/§8) en leest facetten, suggesties en
 * `afgekapt` uit het antwoord. `extern`: de bron kent geen facetten of suggesties; keuzelijsten tonen
 * hun vaste waarden zonder aantallen (CAP-10).
 */
export function useServerTabelData<R, C extends Context>(
  lijst: Lijst<R, C>,
  stand: StandInvoer,
  antwoord: ServerAntwoord | undefined,
  opties: { extern?: boolean } = {},
): TabelData & { parameters: Record<string, string> } {
  const [focus, setFocus] = useState<string | null>(null);
  const extern = opties.extern ?? false;
  const { filters, sortering, pagina, paginagrootte } = stand;

  // Een bewaarde pagina voorbij het servertotaal wordt de laatste (§7), zodra er een antwoord met totaal is.
  const totaal = antwoord?.totaal ?? 0;
  const aantalPaginas = paginas(totaal, paginagrootte);
  const huidig = antwoord?.totaal !== undefined ? Math.min(Math.max(pagina, 1), aantalPaginas) : pagina;

  const parameters = useMemo(() => {
    // Een omgedraaid datumbereik is niet toegepast (§3), dus gaat het ook niet naar de server.
    const toegepast = Object.fromEntries(
      Object.entries(filters).filter(([, f]) => !(typeof f === 'object' && !Array.isArray(f) && datumOmgedraaid(f))),
    );
    const uit: Record<string, string> = { ...standNaarParameters(lijst, toegepast, sortering), page: String(huidig) };
    if (paginagrootte) uit.pageSize = String(paginagrootte);
    if (!extern) {
      const sleutels = keuzelijstSleutels(lijst);
      if (sleutels.length > 0) uit.facetten = sleutels.join(',');
      const tekst = focus ? filters[focus] : undefined;
      if (focus) {
        uit.suggestie = focus;
        uit.suggestieTekst = typeof tekst === 'string' ? tekst : '';
      }
    }
    return uit;
  }, [lijst, filters, sortering, huidig, paginagrootte, extern, focus]);

  const keuzes = useMemo(() => {
    if (!extern) return antwoord?.facetten ?? {};
    const uit: Record<string, Facet[]> = {};
    for (const k of lijst.kolommen) if (k.vasteWaarden) uit[k.sleutel] = k.vasteWaarden.map((waarde) => ({ waarde, aantal: 0 }));
    return uit;
  }, [extern, antwoord?.facetten, lijst]);

  return {
    parameters,
    keuzes,
    suggesties: (sleutel, tekst) => {
      if (extern || sleutel !== focus || !antwoord?.suggesties) return GEEN;
      // Zolang het volgende antwoord onderweg is, alleen wat nog bij de getypte tekst past.
      const term = tekst.trim().toLocaleLowerCase('nl');
      return { ...antwoord.suggesties, waarden: antwoord.suggesties.waarden.filter((w) => w.toLocaleLowerCase('nl').includes(term)) };
    },
    afgekapt: antwoord?.afgekapt ?? false,
    zonderAantallen: extern,
    vertraging: 300,
    totaal,
    pagina: huidig,
    paginas: aantalPaginas,
    opTekstFocus: extern ? undefined : setFocus,
  };
}
